import { useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { cn } from '@/lib/utils';
import type { CalendarField } from '@/services/bookingCalendarService';
import { buildDayBlocks, type DayBlock } from '@/components/admin/booking/bookingCalendarBlocks';
import {
  fromMinutes,
  isSlotOngoing,
  isSlotPast,
  toDateKey,
  toMinutes,
  weekdayShort,
} from '@/components/admin/booking/bookingCalendarUtils';

interface BookingWeekGridProps {
  fields: CalendarField[];
  days: Date[];
  hours: string[];
  stepMinutes?: number;
  selected: Set<string>;
  now: Date;
  draft?: { fieldSlug: string; fieldName: string; keys: string[] } | null;
  onSlot: (field: CalendarField, block: DayBlock) => void;
  onFocusDay?: (date: Date) => void;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

function formatFieldShortName(name: string): string {
  if (/^lapangan\s+/i.test(name)) {
    return name.replace(/^lapangan\s+/i, 'Lap ');
  }
  return name;
}

function getBookingStyle(paymentStatus?: string, isOngoing?: boolean, isPast?: boolean) {
  if (isOngoing) {
    return {
      card: 'bg-[#FC480D] border border-[#FC480D] text-white hover:bg-[#E8490F]',
      field: 'text-white/80 font-semibold',
      name: 'text-white font-semibold',
    };
  }
  if (isPast) {
    return {
      card: 'bg-[#F5F6F7] border border-[#E0E6ED] text-gray-700 hover:bg-gray-100',
      field: 'text-gray-500 font-semibold',
      name: 'text-gray-800 font-semibold',
    };
  }
  const s = (paymentStatus || '').toLowerCase();
  if (s === 'paid' || s === 'lunas' || s === 'success' || s === 'settled') {
    return {
      card: 'bg-[#ECFDF3] border border-[#A6F4C5] text-[#027A48] hover:bg-[#D1FADF]',
      field: 'text-[#027A48] font-semibold',
      name: 'text-[#054F31] font-semibold',
    };
  }
  if (s === 'dp' || s === 'dp_paid' || s === 'partial') {
    return {
      card: 'bg-[#FFF5EE] border border-[#FC480D]/40 text-[#FC480D] hover:bg-orange-100/60',
      field: 'text-[#FC480D] font-semibold',
      name: 'text-[#C43200] font-semibold',
    };
  }
  // Unpaid / Pending / Waiting
  return {
    card: 'bg-[#FEF3F2] border border-dashed border-[#FECDCA] text-[#B42318] hover:bg-[#FEE4E2]',
    field: 'text-[#B42318] font-semibold',
    name: 'text-[#912018] font-semibold',
  };
}

function WeekLegend() {
  return (
    <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:items-center sm:justify-between text-xs">
      <div className="flex flex-wrap items-center gap-4 text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-gray-400 bg-gray-200" />
          Selesai
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FC480D]" />
          Berlangsung
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-[#0BCF46] bg-[#ECFDF3]" />
          Paid
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-[#FC480D] bg-[#FFF5EE]" />
          DP
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-dashed border-[#E7515A] bg-[#FEF2F2]" />
          Unpaid
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-gray-200" />
          Sudah lewat
        </span>
      </div>
      <p className="text-gray-400">Klik kartu untuk detail · Klik header tanggal untuk jadwal harian</p>
    </div>
  );
}

function findBlockInRow(
  blocks: Map<string, DayBlock> | undefined,
  rowStartMin: number,
  rowEndMin: number,
): DayBlock | undefined {
  if (!blocks) return undefined;
  const exact = blocks.get(fromMinutes(rowStartMin));
  if (exact) return exact;
  for (const b of blocks.values()) {
    const bStart = toMinutes(b.start);
    if (bStart >= rowStartMin && bStart < rowEndMin) {
      return b;
    }
  }
  return undefined;
}

export default function BookingWeekGrid({
  fields,
  days,
  hours,
  stepMinutes = 60,
  selected,
  now,
  draft,
  onSlot,
  onFocusDay,
}: BookingWeekGridProps) {
  const todayKey = toDateKey(now);
  const safeStep = stepMinutes > 0 ? stepMinutes : 60;
  const displayHours =
    hours.length > 0
      ? hours
      : [
          '07:00',
          '08:00',
          '09:00',
          '10:00',
          '11:00',
          '12:00',
          '13:00',
          '14:00',
          '15:00',
          '16:00',
          '17:00',
          '18:00',
          '19:00',
          '20:00',
          '21:00',
          '22:00',
        ];

  // Pre-build blocks for each field and day
  const gridData = useMemo(() => {
    const map = new Map<string, Map<string, Map<string, DayBlock>>>();

    days.forEach((day) => {
      const dateKey = toDateKey(day);
      const fieldMap = new Map<string, Map<string, DayBlock>>();

      fields.forEach((field) => {
        const blocks = buildDayBlocks(field.slots, dateKey, selected, safeStep);
        fieldMap.set(field.slug, blocks);
      });

      map.set(dateKey, fieldMap);
    });

    return map;
  }, [days, fields, selected, safeStep]);

  const getCellBookings = useCallback(
    (dateKey: string, rowStartMin: number, rowEndMin: number) => {
      const fieldMap = gridData.get(dateKey);
      if (!fieldMap) return [];

      const list: {
        field: CalendarField;
        block: DayBlock;
        isContinuation: boolean;
      }[] = [];

      fields.forEach((field) => {
        const blocks = fieldMap.get(field.slug);
        if (!blocks) return;

        for (const b of blocks.values()) {
          if (b.kind !== 'booked') continue;
          const bStart = toMinutes(b.start);
          const bEnd = toMinutes(b.end);
          if (bStart >= rowStartMin && bStart < rowEndMin) {
            list.push({ field, block: b, isContinuation: false });
          } else if (bStart < rowStartMin && bEnd > rowStartMin) {
            list.push({ field, block: b, isContinuation: true });
          }
        }
      });

      list.sort((a, b) => a.field.name.localeCompare(b.field.name, undefined, { numeric: true }));
      return list;
    },
    [gridData, fields]
  );

  const handleEmptySlotClick = (day: Date, rowStartMin: number, rowEndMin: number, rowEnd: string) => {
    if (draft) {
      const targetField = fields.find((f) => f.slug === draft.fieldSlug);
      if (targetField) {
        const dateKey = toDateKey(day);
        const blocks = gridData.get(dateKey)?.get(targetField.slug);
        const block = findBlockInRow(blocks, rowStartMin, rowEndMin);
        if (block) {
          onSlot(targetField, block);
          return;
        }
      }
      return;
    }

    const dateKey = toDateKey(day);
    if (isSlotPast(dateKey, rowEnd, now)) {
      return;
    }

    const fieldMap = gridData.get(dateKey);
    if (fieldMap) {
      for (const field of fields) {
        const blocks = fieldMap.get(field.slug);
        const block = findBlockInRow(blocks, rowStartMin, rowEndMin);
        if (block && block.kind === 'open') {
          if (!isSlotPast(dateKey, block.end, now)) {
            onSlot(field, block);
            return;
          }
        }
      }
      // Jika semua lapangan tutup atau tidak ada sesi terbuka pada jam ini
      const hasAnyOpen = fields.some((field) => {
        const b = findBlockInRow(fieldMap.get(field.slug), rowStartMin, rowEndMin);
        return b && b.kind === 'open' && !isSlotPast(dateKey, b.end, now);
      });
      if (!hasAnyOpen) {
        return;
      }
    }
    onFocusDay?.(day);
  };

  return (
    <div className="space-y-3">
      {/* Scrollable calendar table container */}
      <div className="overflow-x-auto rounded-2xl border border-[#E0E6ED] bg-white shadow-2xs">
        <table className="w-full min-w-[980px] border-collapse table-fixed text-left">
          {/* Header Row: Days of the week */}
          <thead>
            <tr className="border-b border-[#E0E6ED] bg-white">
              {/* Top-left corner for hours column */}
              <th className="w-16 sm:w-20 border-r border-[#E0E6ED] p-2 bg-white" />

              {/* 7 Days Columns */}
              {days.map((day) => {
                const dateKey = toDateKey(day);
                const isToday = dateKey === todayKey;

                return (
                  <th
                    key={dateKey}
                    onClick={() => {
                      if (draft) return;
                      onFocusDay?.(day);
                    }}
                    className={cn(
                      'p-2 text-center border-r border-[#E0E6ED] last:border-r-0 transition-colors select-none',
                      !draft ? 'cursor-pointer hover:bg-gray-50/80' : 'cursor-default',
                      isToday && 'bg-[#FFF5EE]/30'
                    )}
                    title={draft ? undefined : 'Klik untuk melihat detail jadwal harian'}
                  >
                    {isToday ? (
                      <div className="bg-[#FFF5EE] border border-orange-200/60 rounded-xl py-1.5 px-2">
                        <span className="block text-[11px] font-semibold text-[#FC480D] leading-tight">
                          Hari ini
                        </span>
                        <span className="block text-xs sm:text-sm font-semibold text-[#FC480D] leading-tight mt-0.5">
                          {day.getDate()} {MONTH_SHORT[day.getMonth()]}
                        </span>
                      </div>
                    ) : (
                      <div className="py-1">
                        <span className="block text-[11px] font-medium text-gray-400 leading-tight">
                          {weekdayShort(day)}
                        </span>
                        <span className="block text-xs sm:text-sm font-semibold text-[#0E1726] leading-tight mt-0.5">
                          {day.getDate()} {MONTH_SHORT[day.getMonth()]}
                        </span>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Rows: Each Hour in hours */}
          <tbody>
            {displayHours.map((hour, rowIndex) => {
              const rowStartMin = toMinutes(hour);
              const nextHour = displayHours[rowIndex + 1];
              const rowEndMin = nextHour ? toMinutes(nextHour) : rowStartMin + safeStep;
              const rowEnd = nextHour || fromMinutes(rowEndMin);

              return (
              <tr key={hour} className="border-b border-[#E0E6ED] last:border-b-0">
                {/* Side Hour Cell on Left */}
                <td className="w-16 sm:w-20 border-r border-[#E0E6ED] p-2 align-top text-center select-none bg-white">
                  <span className="text-xs font-semibold text-gray-400 pt-1 block">
                    {hour.replace(':', '.')}
                  </span>
                </td>

                {/* 7 Day cells */}
                {days.map((day) => {
                  const dateKey = toDateKey(day);
                  const isToday = dateKey === todayKey;
                  const cellBookings = getCellBookings(dateKey, rowStartMin, rowEndMin);

                  const MAX_VISIBLE = 3;
                  const visibleBookings = cellBookings.slice(0, MAX_VISIBLE);
                  const overflowCount = cellBookings.length - MAX_VISIBLE;

                  // Mode Custom Session Draft
                  const isDraft = Boolean(draft);
                  const draftField = isDraft ? fields.find((f) => f.slug === draft?.fieldSlug) || null : null;
                  const draftBlock = draftField
                    ? findBlockInRow(gridData.get(dateKey)?.get(draftField.slug), rowStartMin, rowEndMin) ?? null
                    : null;
                  const isDraftSlotSelected = draftBlock?.kind === 'selected';
                  const isDraftSlotBooked = draftBlock?.kind === 'booked';

                  return (
                    <td
                      key={dateKey}
                      className={cn(
                        'border-r border-[#E0E6ED] last:border-r-0 p-1.5 align-top transition-colors min-h-[90px]',
                        isToday && 'bg-[#FFF5EE]/15'
                      )}
                    >
                      {isDraft && draftField && draftBlock ? (
                        isDraftSlotBooked ? (
                          // Tampilkan booking yang sudah ada pada lapangan draft
                          <div className="space-y-1.5 min-h-[72px]">
                            {visibleBookings.map(({ field, block, isContinuation }) => {
                              const slot = block.slots[0];
                              const isOngoing = isSlotOngoing(dateKey, block.start, block.end, now);
                              const isPast = isSlotPast(dateKey, block.end, now);
                              const styles = getBookingStyle(slot?.payment_status, isOngoing, isPast);
                              const fieldLabel = formatFieldShortName(field.name);
                              const customerLabel = slot?.customer_name || 'Booking';

                              return (
                                <button
                                  key={`${field.slug}-${slot?.public_id || block.start}`}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSlot(field, block);
                                  }}
                                  className={cn(
                                    'w-full rounded-xl p-2 text-left transition-all shadow-2xs hover:shadow-xs active:scale-[0.99] cursor-pointer block',
                                    styles.card
                                  )}
                                  title={`${field.name} · ${customerLabel} (${block.start} - ${block.end})`}
                                >
                                  <div className={cn('text-[11px] font-semibold leading-tight truncate', styles.field)}>
                                    {fieldLabel}{' '}
                                    {isContinuation && (
                                      <span className="opacity-70 font-normal text-[10px]">(Lanjutan)</span>
                                    )}
                                  </div>
                                  <div className={cn('text-xs font-semibold leading-tight truncate mt-0.5', styles.name)}>
                                    {customerLabel}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ) : isDraftSlotSelected ? (
                          // Sesi ini dipilih untuk dibuka
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSlot(draftField, draftBlock);
                            }}
                            className="w-full min-h-[72px] rounded-xl p-2.5 text-left border-2 border-[#FC480D] bg-[#FFF5EE] shadow-2xs hover:bg-[#FFEADA] transition-all flex flex-col justify-between cursor-pointer group"
                            title="Sesi ini dipilih untuk dibuka. Klik untuk membatalkan."
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-semibold text-[#FC480D] flex items-center gap-1.5">
                                <Icon icon="ph:check-circle-fill" className="w-4 h-4 text-[#FC480D]" />
                                Buka Sesi
                              </span>
                              <span className="text-[10px] text-gray-500 font-medium">{hour.replace(':', '.')}</span>
                            </div>
                            <span className="text-[10px] text-[#FC480D]/90 font-medium">Klik untuk batal</span>
                          </button>
                        ) : (
                          // Sesi ini tertutup / belum dipilih
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSlot(draftField, draftBlock);
                            }}
                            className="w-full min-h-[72px] rounded-xl p-2.5 text-left border border-dashed border-[#D0D5DD] bg-gray-50/60 hover:bg-orange-50/50 hover:border-[#FC480D]/60 transition-all flex flex-col justify-between cursor-pointer group"
                            title="Sesi ini tutup. Klik untuk membuka sesi pada jam ini."
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-medium text-gray-400 group-hover:text-[#FC480D] flex items-center gap-1.5 transition-colors">
                                <Icon icon="ph:lock-simple" className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#FC480D]" />
                                Tutup
                              </span>
                              <span className="text-[10px] text-gray-400 font-medium">{hour.replace(':', '.')}</span>
                            </div>
                            <span className="text-[11px] font-semibold text-[#FC480D] opacity-0 group-hover:opacity-100 transition-opacity">
                              + Buka Sesi
                            </span>
                          </button>
                        )
                      ) : cellBookings.length === 0 ? (
                        (() => {
                          const rowBlocks = fields
                            .map((field) => findBlockInRow(gridData.get(dateKey)?.get(field.slug), rowStartMin, rowEndMin))
                            .filter((b): b is DayBlock => Boolean(b));
                          const latestEnd = rowBlocks.reduce(
                            (acc, b) => (toMinutes(b.end) > toMinutes(acc) ? b.end : acc),
                            rowEnd,
                          );
                          const isPast = isSlotPast(dateKey, latestEnd, now);
                          const hasOpenBlock = rowBlocks.some(
                            (b) => b.kind === 'open' && !isSlotPast(dateKey, b.end, now),
                          );
                          const isAllClosed =
                            fields.length > 0 &&
                            !hasOpenBlock &&
                            rowBlocks.length > 0 &&
                            rowBlocks.every((b) => b.kind === 'blocked' || b.kind === 'locked');
                          const hasNoSession = fields.length > 0 && rowBlocks.length === 0;

                          if (isPast) {
                            return (
                              <div
                                className="h-full min-h-[72px] rounded-lg bg-gray-50/40 flex items-center justify-center cursor-not-allowed select-none"
                                title="Waktu ini telah terlewat"
                              >
                                <span className="text-[10px] text-gray-300 font-medium">Terlewat</span>
                              </div>
                            );
                          }

                          if (isAllClosed || hasNoSession) {
                            return (
                              <div
                                className="h-full min-h-[72px] rounded-lg bg-gray-50/60 border border-dashed border-gray-200/80 flex items-center justify-center cursor-not-allowed select-none flex-col gap-1 p-2"
                                title="Sesi lapangan tutup pada jam ini"
                              >
                                <Icon icon="ph:lock-simple" className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-[10px] text-gray-400 font-medium">Tutup</span>
                              </div>
                            );
                          }

                          return (
                            <div
                              onClick={() => handleEmptySlotClick(day, rowStartMin, rowEndMin, rowEnd)}
                              className="h-full min-h-[72px] rounded-lg transition-colors hover:bg-gray-50/70 cursor-pointer flex items-center justify-center group"
                              title="Klik untuk membuat booking baru"
                            >
                              <span className="text-[11px] text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                                + Booking
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="space-y-1.5 min-h-[72px]">
                          {visibleBookings.map(({ field, block, isContinuation }) => {
                            const slot = block.slots[0];
                            const isOngoing = isSlotOngoing(dateKey, block.start, block.end, now);
                            const isPast = isSlotPast(dateKey, block.end, now);
                            const styles = getBookingStyle(slot?.payment_status, isOngoing, isPast);
                            const fieldLabel = formatFieldShortName(field.name);
                            const customerLabel = slot?.customer_name || 'Booking';

                            return (
                              <button
                                key={`${field.slug}-${slot?.public_id || block.start}`}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSlot(field, block);
                                }}
                                className={cn(
                                  'w-full rounded-xl p-2 text-left transition-all shadow-2xs hover:shadow-xs active:scale-[0.99] cursor-pointer block',
                                  styles.card
                                )}
                                title={`${field.name} · ${customerLabel} (${block.start} - ${block.end})`}
                              >
                                <div className={cn('text-[11px] font-semibold leading-tight truncate', styles.field)}>
                                  {fieldLabel}{' '}
                                  {isContinuation && (
                                    <span className="opacity-70 font-normal text-[10px]">(Lanjutan)</span>
                                  )}
                                </div>
                                <div className={cn('text-xs font-semibold leading-tight truncate mt-0.5', styles.name)}>
                                  {customerLabel}
                                </div>
                              </button>
                            );
                          })}

                          {overflowCount > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onFocusDay?.(day);
                              }}
                              className="text-[11px] font-semibold text-[#FC480D] hover:underline block text-left pt-0.5 px-1 cursor-pointer"
                            >
                              +{overflowCount} lainnya
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend below the table */}
      {draft ? (
        <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:items-center sm:justify-between text-xs">
          <div className="flex flex-wrap items-center gap-4 text-gray-600">
            <span className="inline-flex items-center gap-1.5 font-semibold text-[#FC480D]">
              <Icon icon="ph:check-circle-fill" className="h-4 w-4 text-[#FC480D]" />
              Sesi Akan Dibuka ({draft.keys.length})
            </span>
            <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium">
              <Icon icon="ph:lock-simple" className="h-3.5 w-3.5 text-gray-400" />
              Sesi Tutup (Klik untuk buka)
            </span>
            <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium">
              <span className="h-2.5 w-2.5 rounded-full border border-gray-400 bg-gray-200" />
              Sudah Ada Booking
            </span>
          </div>
          <p className="text-gray-400">Klik kotak jam untuk memilih atau membatalkan sesi yang ingin dibuka</p>
        </div>
      ) : (
        <WeekLegend />
      )}
    </div>
  );
}
